import React, { useState, useCallback } from 'react';
import * as XLSX from '@e965/xlsx';
import * as pdfjsLib from 'pdfjs-dist';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent } from '@/components/ui/card';
import { Upload, FileSpreadsheet, FileText, AlertCircle, CheckCircle2, X, Download } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// Set up PDF.js worker (v4+ uses .mjs)
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

interface CreatorImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

interface ParsedRow {
  name: string;
  instagramHandle: string | null;
  tiktokHandle: string | null;
  youtubeHandle: string | null;
  totalReach: number | null;
  description: string | null;
  errors: string[];
}

interface ColumnMapping {
  name: string;
  instagram: string;
  tiktok: string;
  youtube: string;
  reach: string;
  description: string;
}

type ImportStep = 'upload' | 'mapping' | 'preview' | 'importing' | 'complete';
type ImportSource = 'excel' | 'pdf';

export const CreatorImportModal: React.FC<CreatorImportModalProps> = ({
  isOpen,
  onClose,
  onImportComplete,
}) => {
  const { toast } = useToast();
  const [step, setStep] = useState<ImportStep>('upload');
  const [rawData, setRawData] = useState<any[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({
    name: '',
    instagram: '',
    tiktok: '',
    youtube: '',
    reach: '',
    description: '',
  });
  const [parsedData, setParsedData] = useState<ParsedRow[]>([]);
  const [importOptions, setImportOptions] = useState({
    autoGroup: true,
    skipDuplicates: true,
  });
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState<{
    imported: number;
    skipped: number;
    grouped: number;
    linked: number;
    errors: string[];
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [importSource, setImportSource] = useState<ImportSource>('excel');

  const resetModal = () => {
    setStep('upload');
    setRawData([]);
    setColumns([]);
    setColumnMapping({
      name: '',
      instagram: '',
      tiktok: '',
      youtube: '',
      reach: '',
      description: '',
    });
    setParsedData([]);
    setImportProgress(0);
    setImportResults(null);
    setImportSource('excel');
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  // Parse follower count strings like "143.4M", "458K", "1.2B"
  const parseFollowerCount = (value: string | number | null): number | null => {
    if (value === null || value === undefined || value === '') return null;
    
    if (typeof value === 'number') return Math.round(value);
    
    const str = value.toString().trim().toUpperCase();
    const match = str.match(/^([\d.]+)\s*([KMB])?$/);
    
    if (!match) {
      // Try parsing as plain number
      const num = parseFloat(str.replace(/,/g, ''));
      return isNaN(num) ? null : Math.round(num);
    }
    
    const num = parseFloat(match[1]);
    const suffix = match[2];
    
    if (isNaN(num)) return null;
    
    switch (suffix) {
      case 'K': return Math.round(num * 1000);
      case 'M': return Math.round(num * 1000000);
      case 'B': return Math.round(num * 1000000000);
      default: return Math.round(num);
    }
  };

  // Extract username from various handle formats
  const extractUsername = (value: string | null, platform: 'instagram' | 'tiktok' | 'youtube'): string | null => {
    if (!value || typeof value !== 'string') return null;
    
    const str = value.trim();
    if (!str) return null;
    
    // Handle IG_username or TT_username format
    const prefixMatch = str.match(/^(IG|TT|YT)_(.+)$/i);
    if (prefixMatch) {
      const prefix = prefixMatch[1].toUpperCase();
      const username = prefixMatch[2];
      if (
        (prefix === 'IG' && platform === 'instagram') ||
        (prefix === 'TT' && platform === 'tiktok') ||
        (prefix === 'YT' && platform === 'youtube')
      ) {
        return username.replace(/^@/, '');
      }
      return null;
    }
    
    // Handle full URLs
    if (str.includes('instagram.com')) {
      const match = str.match(/instagram\.com\/([^/?]+)/);
      return platform === 'instagram' && match ? match[1].replace(/^@/, '') : null;
    }
    if (str.includes('tiktok.com')) {
      const match = str.match(/tiktok\.com\/@?([^/?]+)/);
      return platform === 'tiktok' && match ? match[1].replace(/^@/, '') : null;
    }
    if (str.includes('youtube.com') || str.includes('youtu.be')) {
      const match = str.match(/(?:youtube\.com\/(?:@|channel\/|c\/)?|youtu\.be\/)([^/?]+)/);
      return platform === 'youtube' && match ? match[1].replace(/^@/, '') : null;
    }
    
    // Handle @username format
    if (str.startsWith('@')) {
      return str.substring(1);
    }
    
    // Return as-is if it looks like a username
    if (/^[\w.]+$/.test(str)) {
      return str;
    }
    
    return str;
  };

  // Extract handle from prefixed format like "IG_username" or "TT_username"
  const extractPrefixedHandle = (value: string): { platform: 'instagram' | 'tiktok' | 'youtube' | null; username: string } => {
    const str = value.trim();
    
    // Check for IG_ prefix
    if (str.match(/^IG[_-]/i)) {
      return { platform: 'instagram', username: str.replace(/^IG[_-]/i, '').replace(/^@/, '') };
    }
    // Check for TT_ prefix
    if (str.match(/^TT[_-]/i)) {
      return { platform: 'tiktok', username: str.replace(/^TT[_-]/i, '').replace(/^@/, '') };
    }
    // Check for YT_ prefix
    if (str.match(/^YT[_-]/i)) {
      return { platform: 'youtube', username: str.replace(/^YT[_-]/i, '').replace(/^@/, '') };
    }
    
    // Check for URLs
    if (str.includes('instagram.com')) {
      const match = str.match(/instagram\.com\/([^/?]+)/);
      return { platform: 'instagram', username: match ? match[1].replace(/^@/, '') : str };
    }
    if (str.includes('tiktok.com')) {
      const match = str.match(/tiktok\.com\/@?([^/?]+)/);
      return { platform: 'tiktok', username: match ? match[1].replace(/^@/, '') : str };
    }
    if (str.includes('youtube.com') || str.includes('youtu.be')) {
      const match = str.match(/(?:youtube\.com\/(?:@|channel\/|c\/)?|youtu\.be\/)([^/?]+)/);
      return { platform: 'youtube', username: match ? match[1].replace(/^@/, '') : str };
    }
    
    return { platform: null, username: str.replace(/^@/, '') };
  };

  const autoDetectColumns = (cols: string[]) => {
    const mapping: ColumnMapping = {
      name: '',
      instagram: '',
      tiktok: '',
      youtube: '',
      reach: '',
      description: '',
    };
    
    cols.forEach(col => {
      const lower = col.toLowerCase();
      if (lower.includes('name') && !mapping.name) {
        mapping.name = col;
      } else if ((lower.includes('instagram') || lower.includes('ig') || lower === 'insta') && !mapping.instagram) {
        mapping.instagram = col;
      } else if ((lower.includes('tiktok') || lower.includes('tt')) && !mapping.tiktok) {
        mapping.tiktok = col;
      } else if ((lower.includes('youtube') || lower.includes('yt')) && !mapping.youtube) {
        mapping.youtube = col;
      } else if ((lower.includes('reach') || lower.includes('follower') || lower.includes('total')) && !mapping.reach) {
        mapping.reach = col;
      } else if ((lower.includes('description') || lower.includes('bio') || lower.includes('note')) && !mapping.description) {
        mapping.description = col;
      }
    });
    
    return mapping;
  };

  const handleFileUpload = useCallback((file: File) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        
        if (jsonData.length < 2) {
          toast({
            title: 'Invalid file',
            description: 'The file must have at least a header row and one data row.',
            variant: 'destructive',
          });
          return;
        }
        
        const headers = (jsonData[0] as string[]).map(h => h?.toString() || '');
        const rows = jsonData.slice(1).map(row => {
          const obj: Record<string, any> = {};
          headers.forEach((header, i) => {
            obj[header] = (row as any[])[i];
          });
          return obj;
        });
        
        setColumns(headers.filter(h => h));
        setRawData(rows);
        setColumnMapping(autoDetectColumns(headers));
        setImportSource('excel');
        setStep('mapping');
        
        toast({
          title: 'File loaded',
          description: `Found ${rows.length} rows with ${headers.length} columns.`,
        });
      } catch (error) {
        console.error('Error parsing file:', error);
        toast({
          title: 'Error parsing file',
          description: 'Please ensure the file is a valid Excel or CSV file.',
          variant: 'destructive',
        });
      }
    };
    
    reader.readAsBinaryString(file);
  }, [toast]);

  // PDF parsing logic
  const handlePDFUpload = useCallback(async (file: File) => {
    try {
      toast({
        title: 'Processing PDF',
        description: 'Extracting creator data from PDF...',
      });

      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      const allCreators: ParsedRow[] = [];
      
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        
        // Extract text items with position info
        const items = textContent.items as any[];
        
        // Group items by approximate Y position (rows)
        const rows: Map<number, any[]> = new Map();
        items.forEach(item => {
          if (!item.str?.trim()) return;
          const y = Math.round(item.transform[5] / 10) * 10; // Round to nearest 10
          if (!rows.has(y)) rows.set(y, []);
          rows.get(y)!.push(item);
        });
        
        // Sort rows by Y position (top to bottom)
        const sortedYs = Array.from(rows.keys()).sort((a, b) => b - a);
        
        let currentCreator: Partial<ParsedRow> | null = null;
        
        for (const y of sortedYs) {
          const rowItems = rows.get(y)!.sort((a, b) => a.transform[4] - b.transform[4]);
          const rowText = rowItems.map(item => item.str.trim()).filter(Boolean);
          const fullRowText = rowText.join(' ');
          
          // Skip header rows
          if (fullRowText.toLowerCase().includes('name') && 
              (fullRowText.toLowerCase().includes('instagram') || fullRowText.toLowerCase().includes('tiktok'))) {
            continue;
          }
          
          // Try to detect name (usually starts a row, capitalized, no @ or URL)
          const possibleName = rowText[0];
          const isLikelyName = possibleName && 
            /^[A-Z][a-zA-Z\s'-]+$/.test(possibleName) && 
            !possibleName.includes('@') && 
            !possibleName.includes('_') &&
            !possibleName.includes('http') &&
            possibleName.length > 2 &&
            possibleName.length < 50;
          
          if (isLikelyName) {
            // Save previous creator if exists
            if (currentCreator?.name) {
              allCreators.push({
                name: currentCreator.name,
                instagramHandle: currentCreator.instagramHandle || null,
                tiktokHandle: currentCreator.tiktokHandle || null,
                youtubeHandle: currentCreator.youtubeHandle || null,
                totalReach: currentCreator.totalReach || null,
                description: currentCreator.description || null,
                errors: [],
              });
            }
            
            currentCreator = { name: possibleName };
            
            // Process remaining items in the row for handles
            for (let i = 1; i < rowText.length; i++) {
              const item = rowText[i];
              const { platform, username } = extractPrefixedHandle(item);
              
              if (platform === 'instagram' && !currentCreator.instagramHandle) {
                currentCreator.instagramHandle = username;
              } else if (platform === 'tiktok' && !currentCreator.tiktokHandle) {
                currentCreator.tiktokHandle = username;
              } else if (platform === 'youtube' && !currentCreator.youtubeHandle) {
                currentCreator.youtubeHandle = username;
              } else if (!platform && /^[\d,.]+[KMB]?$/i.test(item)) {
                currentCreator.totalReach = parseFollowerCount(item);
              }
            }
          } else if (currentCreator) {
            // Continue parsing handles for current creator
            for (const item of rowText) {
              const { platform, username } = extractPrefixedHandle(item);
              
              if (platform === 'instagram' && !currentCreator.instagramHandle) {
                currentCreator.instagramHandle = username;
              } else if (platform === 'tiktok' && !currentCreator.tiktokHandle) {
                currentCreator.tiktokHandle = username;
              } else if (platform === 'youtube' && !currentCreator.youtubeHandle) {
                currentCreator.youtubeHandle = username;
              } else if (!platform && /^[\d,.]+[KMB]?$/i.test(item)) {
                currentCreator.totalReach = parseFollowerCount(item);
              }
            }
          }
        }
        
        // Don't forget the last creator
        if (currentCreator?.name) {
          allCreators.push({
            name: currentCreator.name,
            instagramHandle: currentCreator.instagramHandle || null,
            tiktokHandle: currentCreator.tiktokHandle || null,
            youtubeHandle: currentCreator.youtubeHandle || null,
            totalReach: currentCreator.totalReach || null,
            description: currentCreator.description || null,
            errors: [],
          });
        }
      }
      
      // Merge duplicate names and add validation errors
      const mergedCreators = mergeCreatorsByName(allCreators);
      
      // Add errors for rows with no handles
      mergedCreators.forEach(creator => {
        if (!creator.instagramHandle && !creator.tiktokHandle && !creator.youtubeHandle) {
          creator.errors.push('No valid platform handles found');
        }
      });
      
      setParsedData(mergedCreators);
      setImportSource('pdf');
      setStep('preview'); // Skip mapping step for PDF
      
      toast({
        title: 'PDF processed',
        description: `Found ${mergedCreators.length} creators from ${pdf.numPages} pages.`,
      });
    } catch (error) {
      console.error('Error parsing PDF:', error);
      toast({
        title: 'Error parsing PDF',
        description: 'Failed to extract creator data from the PDF.',
        variant: 'destructive',
      });
    }
  }, [toast]);

  // Merge creators with the same name (combine platform handles)
  const mergeCreatorsByName = (creators: ParsedRow[]): ParsedRow[] => {
    const byName: Record<string, ParsedRow> = {};
    
    creators.forEach(creator => {
      const key = creator.name.toLowerCase().trim();
      if (!key) return;
      
      if (byName[key]) {
        // Merge platform handles
        byName[key].instagramHandle ||= creator.instagramHandle;
        byName[key].tiktokHandle ||= creator.tiktokHandle;
        byName[key].youtubeHandle ||= creator.youtubeHandle;
        byName[key].description ||= creator.description;
        byName[key].totalReach ||= creator.totalReach;
      } else {
        byName[key] = { ...creator };
      }
    });
    
    return Object.values(byName);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      if (file.name.match(/\.pdf$/i)) {
        handlePDFUpload(file);
      } else if (file.name.match(/\.(xlsx|xls|csv)$/i)) {
        handleFileUpload(file);
      } else {
        toast({
          title: 'Invalid file type',
          description: 'Please upload an Excel (.xlsx, .xls), CSV, or PDF file.',
          variant: 'destructive',
        });
      }
    }
  }, [handleFileUpload, handlePDFUpload, toast]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.name.match(/\.pdf$/i)) {
        handlePDFUpload(file);
      } else {
        handleFileUpload(file);
      }
    }
  };

  const parseDataWithMapping = () => {
    const parsed: ParsedRow[] = rawData.map(row => {
      const errors: string[] = [];
      
      const name = row[columnMapping.name]?.toString().trim() || '';
      if (!name) {
        errors.push('Missing name');
      }
      
      const instagramHandle = columnMapping.instagram 
        ? extractUsername(row[columnMapping.instagram]?.toString(), 'instagram')
        : null;
      const tiktokHandle = columnMapping.tiktok
        ? extractUsername(row[columnMapping.tiktok]?.toString(), 'tiktok')
        : null;
      const youtubeHandle = columnMapping.youtube
        ? extractUsername(row[columnMapping.youtube]?.toString(), 'youtube')
        : null;
      
      if (!instagramHandle && !tiktokHandle && !youtubeHandle) {
        errors.push('No valid platform handles found');
      }
      
      const totalReach = columnMapping.reach
        ? parseFollowerCount(row[columnMapping.reach])
        : null;
      
      const description = columnMapping.description
        ? row[columnMapping.description]?.toString().trim() || null
        : null;
      
      return {
        name,
        instagramHandle,
        tiktokHandle,
        youtubeHandle,
        totalReach,
        description,
        errors,
      };
    }).filter(row => row.name || row.instagramHandle || row.tiktokHandle || row.youtubeHandle);
    
    setParsedData(parsed);
    setStep('preview');
  };

  const performImport = async () => {
    setStep('importing');
    setImportProgress(0);
    
    const validRows = parsedData.filter(row => row.errors.length === 0);
    const newCreatorIds: string[] = [];
    const results = {
      imported: 0,
      skipped: 0,
      grouped: 0,
      linked: 0,
      errors: [] as string[],
    };
    
    try {
      // Group rows by name for multi-platform creators
      const groupedByName: Record<string, ParsedRow[]> = {};
      validRows.forEach(row => {
        const key = row.name.toLowerCase().trim();
        if (!groupedByName[key]) {
          groupedByName[key] = [];
        }
        groupedByName[key].push(row);
      });
      
      const totalGroups = Object.keys(groupedByName).length;
      let processedGroups = 0;
      
      for (const [nameKey, rows] of Object.entries(groupedByName)) {
        const primaryRow = rows[0];
        let creatorGroupId: string | null = null;
        
        // Collect all platform handles from all rows with this name
        const platforms: { platform: string; username: string }[] = [];
        rows.forEach(row => {
          if (row.instagramHandle) {
            platforms.push({ platform: 'instagram', username: row.instagramHandle });
          }
          if (row.tiktokHandle) {
            platforms.push({ platform: 'tiktok', username: row.tiktokHandle });
          }
          if (row.youtubeHandle) {
            platforms.push({ platform: 'youtube', username: row.youtubeHandle });
          }
        });
        
        // Remove duplicates
        const uniquePlatforms = platforms.filter((p, i, arr) => 
          arr.findIndex(x => x.platform === p.platform && x.username === p.username) === i
        );
        
        // Create creator group if multiple platforms and autoGroup is enabled
        if (importOptions.autoGroup && uniquePlatforms.length > 1) {
          const { data: groupData, error: groupError } = await supabase
            .from('creator_groups')
            .insert({
              primary_name: primaryRow.name,
              display_name: primaryRow.name,
              bio: primaryRow.description,
              total_followers: primaryRow.totalReach || 0,
            })
            .select('id')
            .single();
          
          if (groupError) {
            console.error('Error creating group:', groupError);
          } else {
            creatorGroupId = groupData.id;
            results.grouped++;
          }
        }
        
        // Insert each platform record
        for (const { platform, username } of uniquePlatforms) {
          // Check for duplicates
          if (importOptions.skipDuplicates) {
            const { data: existing } = await supabase
              .from('creators')
              .select('id')
              .eq('platform', platform)
              .eq('username', username.toLowerCase())
              .single();
            
            if (existing) {
              results.skipped++;
              continue;
            }
          }
          
          const creatorData: any = {
            platform,
            platform_id: `imported_${platform}_${username.toLowerCase()}`,
            username: username.toLowerCase(),
            display_name: primaryRow.name,
            bio: primaryRow.description,
            follower_count: primaryRow.totalReach,
            creator_group_id: creatorGroupId,
            is_linked: false, // Mark as unlinked - needs API lookup
          };
          
          const { data: insertedCreator, error: insertError } = await supabase
            .from('creators')
            .insert(creatorData)
            .select('id')
            .single();
          
          if (insertError) {
            if (insertError.code === '23505') {
              results.skipped++;
            } else {
              console.error('Error inserting creator:', insertError);
              results.errors.push(`Failed to import ${username} on ${platform}: ${insertError.message}`);
            }
          } else {
            results.imported++;
            if (insertedCreator?.id) {
              newCreatorIds.push(insertedCreator.id);
            }
          }
        }
        
        processedGroups++;
        setImportProgress(Math.round((processedGroups / totalGroups) * 100));
      }
      
      // Auto-link newly imported creators via API (in batches with timeout)
      if (newCreatorIds.length > 0) {
        setImportProgress(95);
        const BATCH_SIZE = 10;
        const TIMEOUT_MS = 25000;
        let totalLinked = 0;
        let apiCreditsExhausted = false;

        for (let i = 0; i < newCreatorIds.length && !apiCreditsExhausted; i += BATCH_SIZE) {
          const batch = newCreatorIds.slice(i, i + BATCH_SIZE);
          try {
            const result = await Promise.race([
              supabase.functions.invoke('link-creator-accounts', {
                body: { creatorIds: batch },
              }),
              new Promise<never>((_, reject) =>
                setTimeout(() => reject(new Error('TIMEOUT')), TIMEOUT_MS)
              ),
            ]);
            if (result.data) {
              totalLinked += result.data.linked || 0;
              if (result.data.apiCreditsExhausted) {
                apiCreditsExhausted = true;
                toast({
                  title: 'API Credits Exhausted',
                  description: 'ScrapeCreators API credits are used up. Creators were imported but not all could be linked. Please check your ScrapeCreators billing.',
                  variant: 'destructive',
                });
              }
            }
          } catch (linkError: any) {
            console.error('Auto-link error:', linkError);
            if (linkError?.message === 'TIMEOUT') {
              toast({
                title: 'Linking timed out',
                description: 'Linking took too long. Some accounts may not have been linked. You can manually link them later.',
              });
            } else {
              results.errors.push('Auto-linking accounts failed. You can manually link them later.');
            }
            break; // Stop further batches on error
          }
        }
        results.linked = totalLinked;
      }

      // --- Phase: Set creator group avatars from linked creators ---
      try {
        const { data: linkedCreators } = await supabase
          .from('creators')
          .select('avatar_url, platform, creator_group_id')
          .in('id', newCreatorIds)
          .not('creator_group_id', 'is', null)
          .not('avatar_url', 'is', null);

        if (linkedCreators && linkedCreators.length > 0) {
          const groupMap = new Map<string, { platform: string; avatar_url: string }[]>();
          for (const c of linkedCreators) {
            const gid = c.creator_group_id!;
            if (!groupMap.has(gid)) groupMap.set(gid, []);
            groupMap.get(gid)!.push({ platform: c.platform, avatar_url: c.avatar_url! });
          }

          const platformPriority = ['tiktok', 'instagram', 'youtube'];
          for (const [groupId, entries] of groupMap) {
            let chosen = entries[0].avatar_url;
            for (const p of platformPriority) {
              const match = entries.find(e => e.platform === p);
              if (match) { chosen = match.avatar_url; break; }
            }
            await supabase
              .from('creator_groups')
              .update({ avatar_url: chosen })
              .eq('id', groupId);
          }
        }
      } catch (avatarError) {
        console.error('Failed to set group avatars:', avatarError);
      }

      setImportProgress(100);
      setImportResults(results);
      setStep('complete');
      
      if (results.imported > 0) {
        onImportComplete();
      }
    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: 'Import failed',
        description: 'An unexpected error occurred during import.',
        variant: 'destructive',
      });
      setStep('preview');
    }
  };

  const downloadTemplate = () => {
    const templateData = [
      ['Name', 'Instagram Handle', 'TikTok Handle', 'YouTube Handle', 'Total Reach', 'Description'],
      ['MrBeast', 'IG_mrbeast', 'TT_mrbeast', 'YT_mrbeast', '500M', 'Content creator and philanthropist'],
      ['Charli D\'Amelio', 'https://instagram.com/charlidamelio', '@charlidamelio', '', '200M', 'Dancer and social media star'],
      ['Emma Chamberlain', 'emmachamberlain', 'TT_emmachamberlain', '', '30M', 'YouTuber and podcaster'],
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'creator-import-template.xlsx');
  };

  const renderUploadStep = () => (
    <div className="space-y-4">
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          isDragging ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
        }`}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <div className="flex justify-center gap-2 mb-4">
          <FileSpreadsheet className="h-10 w-10 text-muted-foreground" />
          <FileText className="h-10 w-10 text-muted-foreground" />
        </div>
        <p className="text-lg font-medium mb-2">Drop your Excel, CSV, or PDF file here</p>
        <p className="text-sm text-muted-foreground mb-4">Supports .xlsx, .xls, .csv, and .pdf files</p>
        <div className="flex gap-2 justify-center">
          <Label htmlFor="file-input" className="cursor-pointer">
            <Input
              id="file-input"
              type="file"
              accept=".xlsx,.xls,.csv,.pdf"
              onChange={handleFileInputChange}
              className="hidden"
            />
            <Button type="button" variant="outline" asChild>
              <span><Upload className="h-4 w-4 mr-2" />Browse Files</span>
            </Button>
          </Label>
          <Button type="button" variant="ghost" onClick={downloadTemplate}>
            <Download className="h-4 w-4 mr-2" />
            Download Template
          </Button>
        </div>
      </div>
      
      <Card>
        <CardContent className="pt-4">
          <h4 className="font-medium mb-2">Expected Format</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• <strong>Name:</strong> Creator's display name</li>
            <li>• <strong>Instagram:</strong> IG_username, @username, or full URL</li>
            <li>• <strong>TikTok:</strong> TT_username, @username, or full URL</li>
            <li>• <strong>YouTube:</strong> YT_username or channel URL</li>
            <li>• <strong>Total Reach:</strong> Numbers like "143.4M", "458K", or "1200000"</li>
            <li>• <strong>Description:</strong> Optional bio or notes</li>
          </ul>
          <p className="text-sm text-muted-foreground mt-3 pt-3 border-t">
            <strong>PDF files:</strong> We'll auto-detect tables and extract creator data. Works best with structured tables.
          </p>
        </CardContent>
      </Card>
    </div>
  );

  const renderMappingStep = () => (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Map your spreadsheet columns to the creator fields. Auto-detected mappings are pre-selected.
      </p>
      
      <div className="grid gap-4">
        {[
          { key: 'name' as const, label: 'Name', required: true },
          { key: 'instagram' as const, label: 'Instagram Handle', required: false },
          { key: 'tiktok' as const, label: 'TikTok Handle', required: false },
          { key: 'youtube' as const, label: 'YouTube Handle', required: false },
          { key: 'reach' as const, label: 'Total Reach/Followers', required: false },
          { key: 'description' as const, label: 'Description/Bio', required: false },
        ].map(({ key, label, required }) => (
          <div key={key} className="flex items-center gap-4">
            <Label className="w-40 text-right">
              {label}
              {required && <span className="text-destructive ml-1">*</span>}
            </Label>
            <Select
              value={columnMapping[key] || '_none'}
              onValueChange={(value) => 
                setColumnMapping(prev => ({ ...prev, [key]: value === '_none' ? '' : value }))
              }
            >
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select column" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">-- Not mapped --</SelectItem>
                {columns.map(col => (
                  <SelectItem key={col} value={col}>{col}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ))}
      </div>
      
      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={() => setStep('upload')}>Back</Button>
        <Button 
          onClick={parseDataWithMapping}
          disabled={!columnMapping.name}
        >
          Preview Import
        </Button>
      </div>
    </div>
  );

  const renderPreviewStep = () => {
    const validCount = parsedData.filter(r => r.errors.length === 0).length;
    const errorCount = parsedData.filter(r => r.errors.length > 0).length;
    const multiPlatformCount = parsedData.filter(r => 
      [r.instagramHandle, r.tiktokHandle, r.youtubeHandle].filter(Boolean).length > 1
    ).length;
    
    return (
      <div className="space-y-4">
        <div className="flex gap-4 flex-wrap">
          {importSource === 'pdf' && (
            <Badge variant="secondary" className="gap-1">
              <FileText className="h-3 w-3" />
              Imported from PDF
            </Badge>
          )}
          <Badge variant="outline" className="gap-1">
            <CheckCircle2 className="h-3 w-3 text-green-500" />
            {validCount} Valid
          </Badge>
          {errorCount > 0 && (
            <Badge variant="destructive" className="gap-1">
              <AlertCircle className="h-3 w-3" />
              {errorCount} Errors
            </Badge>
          )}
          {multiPlatformCount > 0 && (
            <Badge variant="secondary">
              {multiPlatformCount} Multi-platform
            </Badge>
          )}
        </div>
        
        <ScrollArea className="h-[300px] border rounded-lg">
          <div className="p-4 space-y-2">
            {parsedData.slice(0, 20).map((row, i) => (
              <div 
                key={i} 
                className={`p-3 rounded-lg text-sm ${
                  row.errors.length > 0 ? 'bg-destructive/10 border border-destructive/20' : 'bg-muted/50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium">{row.name || '(No name)'}</p>
                    <div className="flex gap-2 mt-1 flex-wrap">
                      {row.instagramHandle && (
                        <Badge variant="outline" className="text-xs">IG: @{row.instagramHandle}</Badge>
                      )}
                      {row.tiktokHandle && (
                        <Badge variant="outline" className="text-xs">TT: @{row.tiktokHandle}</Badge>
                      )}
                      {row.youtubeHandle && (
                        <Badge variant="outline" className="text-xs">YT: {row.youtubeHandle}</Badge>
                      )}
                      {row.totalReach && (
                        <Badge variant="secondary" className="text-xs">
                          {row.totalReach >= 1000000 
                            ? `${(row.totalReach / 1000000).toFixed(1)}M` 
                            : row.totalReach >= 1000 
                              ? `${(row.totalReach / 1000).toFixed(1)}K`
                              : row.totalReach} followers
                        </Badge>
                      )}
                    </div>
                  </div>
                  {row.errors.length > 0 && (
                    <div className="text-xs text-destructive">
                      {row.errors.join(', ')}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {parsedData.length > 20 && (
              <p className="text-center text-sm text-muted-foreground py-2">
                ...and {parsedData.length - 20} more rows
              </p>
            )}
          </div>
        </ScrollArea>
        
        <Card>
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="autoGroup" 
                checked={importOptions.autoGroup}
                onCheckedChange={(checked) => 
                  setImportOptions(prev => ({ ...prev, autoGroup: !!checked }))
                }
              />
              <Label htmlFor="autoGroup" className="text-sm cursor-pointer">
                Auto-create groups for multi-platform creators
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="skipDuplicates" 
                checked={importOptions.skipDuplicates}
                onCheckedChange={(checked) => 
                  setImportOptions(prev => ({ ...prev, skipDuplicates: !!checked }))
                }
              />
              <Label htmlFor="skipDuplicates" className="text-sm cursor-pointer">
                Skip duplicates (by username + platform)
              </Label>
            </div>
          </CardContent>
        </Card>
        
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={() => setStep(importSource === 'pdf' ? 'upload' : 'mapping')}>Back</Button>
          <Button 
            onClick={performImport}
            disabled={validCount === 0}
          >
            Import {validCount} Creator{validCount !== 1 ? 's' : ''}
          </Button>
        </div>
      </div>
    );
  };

  const renderImportingStep = () => (
    <div className="space-y-6 py-8">
      <div className="text-center">
        {importSource === 'pdf' ? (
          <FileText className="h-12 w-12 mx-auto mb-4 text-primary animate-pulse" />
        ) : (
          <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-primary animate-pulse" />
        )}
        <p className="text-lg font-medium">
          {importProgress >= 95 ? 'Linking accounts via API...' : 'Importing creators...'}
        </p>
        <p className="text-sm text-muted-foreground">
          {importProgress >= 95 
            ? 'Fetching real profile data for imported creators' 
            : 'Please wait while we process your data'}
        </p>
      </div>
      <Progress value={importProgress} className="w-full" />
      <p className="text-center text-sm text-muted-foreground">{importProgress}% complete</p>
    </div>
  );

  const renderCompleteStep = () => (
    <div className="space-y-6 py-8">
      <div className="text-center">
        <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-500" />
        <p className="text-lg font-medium">Import Complete!</p>
      </div>
      
      {importResults && (
        <div className="grid grid-cols-2 gap-4 text-center">
          <div className="p-4 bg-green-500/10 rounded-lg">
            <p className="text-2xl font-bold text-green-600">{importResults.imported}</p>
            <p className="text-sm text-muted-foreground">Imported</p>
          </div>
          <div className="p-4 bg-blue-500/10 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">{importResults.linked}</p>
            <p className="text-sm text-muted-foreground">Linked via API</p>
          </div>
          <div className="p-4 bg-muted rounded-lg">
            <p className="text-2xl font-bold">{importResults.skipped}</p>
            <p className="text-sm text-muted-foreground">Skipped</p>
          </div>
          <div className="p-4 bg-primary/10 rounded-lg">
            <p className="text-2xl font-bold text-primary">{importResults.grouped}</p>
            <p className="text-sm text-muted-foreground">Groups Created</p>
          </div>
        </div>
      )}
      
      {importResults?.errors && importResults.errors.length > 0 && (
        <Card className="border-destructive/50">
          <CardContent className="pt-4">
            <p className="font-medium text-destructive mb-2">Errors:</p>
            <ul className="text-sm text-muted-foreground space-y-1">
              {importResults.errors.slice(0, 5).map((err, i) => (
                <li key={i}>• {err}</li>
              ))}
              {importResults.errors.length > 5 && (
                <li>...and {importResults.errors.length - 5} more</li>
              )}
            </ul>
          </CardContent>
        </Card>
      )}
      
      <div className="flex justify-center pt-4">
        <Button onClick={handleClose}>Done</Button>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {importSource === 'pdf' ? (
              <FileText className="h-5 w-5" />
            ) : (
              <FileSpreadsheet className="h-5 w-5" />
            )}
            Import Creators
          </DialogTitle>
          <DialogDescription>
            {step === 'upload' && 'Upload an Excel, CSV, or PDF file with creator information.'}
            {step === 'mapping' && 'Map your spreadsheet columns to creator fields.'}
            {step === 'preview' && 'Review the parsed data before importing.'}
            {step === 'importing' && 'Importing creators to the database...'}
            {step === 'complete' && 'Import finished successfully.'}
          </DialogDescription>
        </DialogHeader>
        
        {step === 'upload' && renderUploadStep()}
        {step === 'mapping' && renderMappingStep()}
        {step === 'preview' && renderPreviewStep()}
        {step === 'importing' && renderImportingStep()}
        {step === 'complete' && renderCompleteStep()}
      </DialogContent>
    </Dialog>
  );
};
